import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

const COLLAPSE_AT = 12;
const COLLAPSE_MIN = 15;

const list = { show: { transition: { staggerChildren: 0.035, delayChildren: 0.1 } } };
const row = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

export const ServicePanel = ({ category, index }) => {
  const { id, name, icon: Icon, blurb, items } = category;
  const [expanded, setExpanded] = useState(false);
  const collapsible = items.length > COLLAPSE_MIN;
  const shown = collapsible && !expanded ? items.slice(0, COLLAPSE_AT) : items;

  return (
    <motion.article
      key={id}
      data-testid={`service-card-${id}`}
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24, transition: { duration: 0.25 } }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-[2rem] border border-[rgba(201,162,75,0.25)] bg-[#454538] px-6 py-10 sm:px-12 sm:py-14 lg:px-16 lg:py-16"
    >
      <span className="pointer-events-none absolute -right-6 -top-10 select-none font-brand text-[11rem] leading-none text-[#C9A24B]/[0.07] sm:text-[15rem]">
        {String(index + 1).padStart(2, "0")}
      </span>

      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-xl">
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(201,162,75,0.5)] bg-[#383D33]">
              <Icon className="h-5 w-5 text-[#C9A24B]" />
            </span>
            <p className="font-body text-[11px] font-medium uppercase tracking-[0.3em] text-[#C9A24B]">
              {items.length} {items.length === 1 ? "Service" : "Services"}
            </p>
          </div>
          <h3 className="mt-6 font-brand text-3xl tracking-wide text-[#F2EFE6] sm:text-4xl lg:text-5xl">
            {name}
          </h3>
          <p className="mt-4 font-body text-base leading-relaxed text-[#F2EFE6]/65">{blurb}</p>
        </div>
        <a
          href="#book"
          onClick={() =>
            window.dispatchEvent(new CustomEvent("prefill-booking-category", { detail: id }))
          }
          data-testid={`service-book-link-${id}`}
          className="btn-shimmer group inline-flex shrink-0 items-center gap-3 self-start rounded-full border border-[#C9A24B] px-6 py-3.5 font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#D8C28A] transition-[background-color,color] duration-300 hover:bg-[#C9A24B] hover:text-[#383D33]"
        >
          Book this service
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>

      <motion.ul
        variants={list}
        initial="hidden"
        animate="show"
        data-testid={`service-items-${id}`}
        className="relative mt-12 grid gap-x-12 gap-y-1 sm:grid-cols-2 xl:grid-cols-3"
      >
        {shown.map((item) => (
          <motion.li
            key={item}
            variants={row}
            className="group flex items-center gap-4 border-b border-[rgba(242,239,230,0.1)] py-4 transition-colors duration-300 hover:border-[rgba(201,162,75,0.6)]"
          >
            <span className="h-1.5 w-1.5 shrink-0 rotate-45 border border-[#C9A24B]/70 transition-[background-color,transform] duration-300 group-hover:scale-125 group-hover:bg-[#C9A24B]" />
            <span className="font-body text-[15px] tracking-wide text-[#F2EFE6]/90 transition-colors duration-300 group-hover:text-[#D8C28A] sm:text-base">
              {item}
            </span>
          </motion.li>
        ))}
      </motion.ul>

      <AnimatePresence>
        {collapsible && (
          <motion.div layout className="relative mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`service-toggle-${id}`}
              className="group inline-flex items-center gap-2 rounded-full border border-[rgba(201,162,75,0.35)] px-6 py-3 font-body text-xs font-medium uppercase tracking-[0.2em] text-[#F2EFE6]/80 transition-[border-color,color] duration-300 hover:border-[#C9A24B] hover:text-[#C9A24B]"
            >
              {expanded ? "Show less" : `Show all ${items.length} services`}
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
};
